import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { Header } from "./components/Layout/Header";
import { Footer } from "./components/Layout/Footer";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Landing } from "./pages/Landing";
import { Courses } from "./pages/Courses";
import { CourseDetail } from "./pages/CourseDetail";
import { Login } from "./pages/Login";
import { Signup } from "./pages/Signup";
import { Dashboard } from "./pages/Dashboard";
import { DashboardCourse } from "./pages/DashboardCourse";
import { LessonPlayer } from "./pages/LessonPlayer";
import { LessonQuizPage } from "./pages/LessonQuizPage";
import { Assessment } from "./pages/Assessment";
import { Certificates } from "./pages/Certificates";
import { CertificateGenerate } from "./pages/CertificateGenerate";
import { PaymentSuccess } from "./pages/PaymentSuccess";
import api from "./api/client";

const THEME_PALETTES: Record<string, Record<string, string>> = {
  yellow: { 50: "253 251 245", 100: "249 240 220", 200: "232 212 168", 300: "212 185 106", 400: "201 162 39", 500: "166 133 30" },
  purple: { 50: "246 243 255", 100: "237 233 254", 200: "221 214 254", 300: "196 181 253", 400: "167 139 250", 500: "139 92 246" },
  green: { 50: "240 253 244", 100: "220 252 231", 200: "187 247 208", 300: "134 239 172", 400: "74 222 128", 500: "34 197 94" },
  red: { 50: "254 242 242", 100: "254 226 226", 200: "254 202 202", 300: "252 165 165", 400: "248 113 113", 500: "239 68 68" },
  blue: { 50: "239 246 255", 100: "219 234 254", 200: "191 219 254", 300: "147 197 253", 400: "96 165 250", 500: "59 130 246" },
}

function App() {
  useEffect(() => {
    api
      .get("/settings/admin/get-settings")
      .then((res) => {
        const theme = res?.data?.data?.accentTheme || "yellow";
        const palette = THEME_PALETTES[theme] || THEME_PALETTES.yellow;
        Object.entries(palette).forEach(([key, value]) => {
          document.documentElement.style.setProperty(`--accent-${key}`, value);
        });
      })
      .catch(() => {
        // Keep default CSS variables if settings API is unavailable.
      });
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen flex flex-col">
          <Header />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:slug" element={<CourseDetail />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/courses/:slug"
                element={
                  <ProtectedRoute>
                    <DashboardCourse />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/courses/:slug/lessons/:lessonId"
                element={
                  <ProtectedRoute>
                    <LessonPlayer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/courses/:slug/lessons/:lessonId/quiz"
                element={
                  <ProtectedRoute>
                    <LessonQuizPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/courses/:slug/assessment"
                element={
                  <ProtectedRoute>
                    <Assessment />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/courses/:slug/certificate"
                element={
                  <ProtectedRoute>
                    <CertificateGenerate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/certificates"
                element={
                  <ProtectedRoute>
                    <Certificates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment/success"
                element={
                  <ProtectedRoute>
                    <PaymentSuccess />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
